import { useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck, faCircleExclamation, faEye, faTriangleExclamation, faUpload, faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { Button, Input, Modal, ProgressBar, Select, Stepper } from './ui'
import { ApiError } from '../services/BaseApiService'
import { migrateApiService, type DocxAnalysis, type MigrateFormat } from '../services/migrateApiService'
import { templatesApiService } from '../services/templatesApiService'
import {
  IMPORT_FORMATS, defaultChangeNote, defaultTemplateName, formatBytes, formatLabel,
  mapWithConcurrency,
} from '../utils/templateImport'
import { colors, fontFamily, fontSize, radius, spacing, transition } from '../styles/theme'

/** Uploads run 3 at a time so a 20-file drop does not open 20 sockets at once. */
const ANALYZE_CONCURRENCY = 3

type RowStatus = 'uploading' | 'inspecting' | 'ready' | 'failed' | 'importing' | 'imported'

interface Row {
  id: string
  file: File
  name: string
  /** The user's selection; starts as the detected vendor. */
  format: MigrateFormat
  status: RowStatus
  /** Upload percentage while status is 'uploading' or 'importing'. */
  progress: number
  analysis?: DocxAnalysis
  error?: string
  expanded: boolean
}

interface ImportTemplatesModalProps {
  folderId?: string
  onClose: () => void
  onSuccess: () => void
}

export default function ImportTemplatesModal({ folderId, onClose, onSuccess }: ImportTemplatesModalProps) {
  const [rows, setRows] = useState<Row[]>([])
  const [phase, setPhase] = useState<'select' | 'inspect' | 'review' | 'import'>('select')
  const [changeNote, setChangeNote] = useState('')
  const [changeNoteEdited, setChangeNoteEdited] = useState(false)
  const [rejected, setRejected] = useState<string[]>([])
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  function patchRow(id: string, patch: Partial<Row>) {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)))
  }

  async function acceptFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const all = Array.from(files)
    const docx = all.filter(f => /\.docx$/i.test(f.name))
    setRejected(all.filter(f => !/\.docx$/i.test(f.name)).map(f => f.name))
    if (docx.length === 0) return

    const fresh: Row[] = docx.map((file, i) => ({
      id: `${Date.now()}-${i}-${file.name}`,
      file,
      name: defaultTemplateName(file.name),
      format: 'docmorphia',
      status: 'uploading',
      progress: 0,
      expanded: false,
    }))
    setRows(fresh)
    setError('')
    setPhase('inspect')

    await mapWithConcurrency(fresh, ANALYZE_CONCURRENCY, async row => {
      try {
        const analysis = await migrateApiService.analyzeDocx(row.file, undefined, pct => {
          // Server-side parsing starts once the bytes are up; swap to an indeterminate bar there.
          patchRow(row.id, { progress: pct, status: pct >= 100 ? 'inspecting' : 'uploading' })
        })
        patchRow(row.id, {
          status: 'ready',
          progress: 100,
          analysis,
          format: analysis.effectiveVendor,
        })
      } catch (err) {
        patchRow(row.id, {
          status: 'failed',
          error: err instanceof ApiError ? err.message : 'Inspection failed',
        })
      }
    })

    setPhase('review')
  }

  /** Re-inspects a single row under a user-chosen format; the rest of the list stays usable. */
  async function changeFormat(row: Row, format: MigrateFormat) {
    patchRow(row.id, { format, status: 'inspecting', error: undefined })
    try {
      const analysis = await migrateApiService.analyzeDocx(row.file, format)
      patchRow(row.id, { status: 'ready', analysis })
    } catch (err) {
      patchRow(row.id, {
        status: 'failed',
        error: err instanceof ApiError ? err.message : 'Inspection failed',
      })
    }
  }

  async function preview(row: Row) {
    // Opened synchronously inside the click handler, otherwise the popup blocker kills it.
    const win = window.open('', '_blank')
    try {
      const blob = await migrateApiService.previewDocx(row.file, row.format)
      if (win) win.location.href = URL.createObjectURL(blob)
    } catch (err) {
      win?.close()
      patchRow(row.id, { error: err instanceof ApiError ? err.message : 'Preview failed' })
    }
  }

  const importable = rows.filter(
    r => r.status === 'ready' && !(r.analysis?.clashErrors.length)
  )

  async function runImport() {
    if (importable.length === 0) return
    setError('')
    setPhase('import')

    let failures = 0
    for (const row of importable) {
      patchRow(row.id, { status: 'importing', progress: 0, error: undefined })
      try {
        const note = changeNote.trim() || defaultChangeNote(row.format)
        const { templateId } = await templatesApiService.create({
          name: row.name.trim() || row.file.name,
          folderId: folderId || undefined,
        })
        await templatesApiService.upload(row.file, templateId, note, row.format, pct =>
          patchRow(row.id, { progress: pct })
        )
        patchRow(row.id, { status: 'imported', progress: 100 })
      } catch (err) {
        // One bad file must not abandon the rest of the batch.
        failures++
        patchRow(row.id, {
          status: 'failed',
          error: err instanceof ApiError ? err.message : 'Import failed',
        })
      }
    }

    if (failures === 0) {
      onSuccess()
      return
    }
    // Drop the rows that landed, so a retry only re-sends what failed.
    setRows(prev => prev.filter(r => r.status !== 'imported'))
    setError(`${failures} of ${importable.length} template${importable.length === 1 ? '' : 's'} failed to import. Fix the errors below and retry.`)
    setPhase('review')
  }

  const inspected = rows.filter(r => r.status !== 'uploading' && r.status !== 'inspecting').length
  const imported = rows.filter(r => r.status === 'imported').length
  const stepIndex = phase === 'select' ? 0 : phase === 'inspect' ? 1 : phase === 'review' ? 2 : 3

  return (
    <Modal
      open
      onClose={onClose}
      width={960}
      title="Upload Template(s)"
      subtitle="Drop one or more .docx files. Each is inspected for Carbone, Docmosis, or Handlebars syntax and converted automatically."
      footer={
        phase === 'select' ? (
          <Button variant="ghost" icon={<FontAwesomeIcon icon={faXmark} />} onClick={onClose}>Cancel</Button>
        ) : (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Input
                inputSize="sm"
                placeholder="Change note (applied to every template)"
                value={changeNote}
                onChange={e => { setChangeNote(e.target.value); setChangeNoteEdited(true) }}
                disabled={phase === 'import'}
              />
              {!changeNoteEdited && (
                <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: 3 }}>
                  Defaults to "Imported from &lt;format&gt;" per template.
                </div>
              )}
            </div>
            <Button variant="ghost" icon={<FontAwesomeIcon icon={faXmark} />} onClick={onClose} disabled={phase === 'import'}>
              Cancel
            </Button>
            <Button
              icon={<FontAwesomeIcon icon={faCheck} />}
              onClick={runImport}
              loading={phase === 'import'}
              disabled={phase !== 'review' || importable.length === 0}
            >
              {phase === 'import'
                ? `Importing ${imported + 1} of ${importable.length}…`
                : `Import ${importable.length} template${importable.length === 1 ? '' : 's'}`}
            </Button>
          </>
        )
      }
    >
      <Stepper steps={['Select', 'Inspect', 'Review', 'Import']} current={stepIndex} />

      {error && <Banner tone="error">{error}</Banner>}

      {rejected.length > 0 && (
        <Banner tone="warning">
          Skipped {rejected.length} non-.docx file{rejected.length === 1 ? '' : 's'}: {rejected.join(', ')}
        </Banner>
      )}

      {phase === 'select' ? (
        <>
          <input
            ref={fileRef}
            type="file"
            accept=".docx"
            multiple
            style={{ display: 'none' }}
            onChange={e => acceptFiles(e.target.files)}
          />
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); acceptFiles(e.dataTransfer.files) }}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: spacing.sm,
              padding: spacing.xxl,
              border: `2px dashed ${dragOver ? colors.primary : colors.border}`,
              borderRadius: radius.md,
              background: dragOver ? colors.primaryLight : colors.background,
              cursor: 'pointer',
              transition: `border-color ${transition.fast}, background ${transition.fast}`,
              userSelect: 'none',
            }}
          >
            <FontAwesomeIcon icon={faUpload} style={{ fontSize: 26, color: colors.textMuted }} />
            <div style={{ fontSize: fontSize.base, fontWeight: 500, color: colors.text }}>
              Drag &amp; drop your .docx files here
            </div>
            <div style={{ fontSize: fontSize.sm, color: colors.primary, textDecoration: 'underline' }}>
              or click to browse
            </div>
          </div>
        </>
      ) : (
        <>
          <ProgressBar
            value={phase === 'import'
              ? (imported / Math.max(1, importable.length)) * 100
              : (inspected / Math.max(1, rows.length)) * 100}
            label={phase === 'import' ? 'Importing templates' : 'Inspecting templates'}
            detail={phase === 'import'
              ? `${imported} of ${importable.length}`
              : `${inspected} of ${rows.length}`}
            style={{ marginBottom: spacing.lg }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            {rows.map(row => (
              <RowCard
                key={row.id}
                row={row}
                locked={phase === 'import'}
                onRename={name => patchRow(row.id, { name })}
                onFormat={format => changeFormat(row, format)}
                onToggle={() => patchRow(row.id, { expanded: !row.expanded })}
                onPreview={() => preview(row)}
                onRemove={() => setRows(prev => prev.filter(r => r.id !== row.id))}
              />
            ))}
          </div>
        </>
      )}
    </Modal>
  )
}

interface RowCardProps {
  row: Row
  locked: boolean
  onRename: (name: string) => void
  onFormat: (format: MigrateFormat) => void
  onToggle: () => void
  onPreview: () => void
  onRemove: () => void
}

function RowCard({ row, locked, onRename, onFormat, onToggle, onPreview, onRemove }: RowCardProps) {
  const { analysis } = row
  const busy = row.status === 'uploading' || row.status === 'inspecting' || row.status === 'importing'
  const clashes = analysis?.clashErrors ?? []
  const isDetected = !!analysis && row.format === (analysis.detectedVendor ?? 'docmorphia')
  const monikers = analysis?.monikers ?? []
  const formatted = monikers.filter(m => m.formatters)
  const plain = monikers.filter(m => !m.formatters)
  // Only the mapped conversions; the WARNING entries already render above as warnings.
  const mappings = (analysis?.conversionChanges ?? []).filter(c => !c.startsWith('WARNING:'))

  return (
    <div style={{
      border: `1px solid ${row.status === 'failed' || clashes.length > 0 ? colors.errorBorder : colors.border}`,
      borderRadius: radius.md,
      padding: spacing.md,
      background: row.status === 'imported' ? colors.successLight : colors.surface,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: spacing.md }}>
        <div style={{ flex: 2, minWidth: 0 }}>
          <Input
            inputSize="sm"
            value={row.name}
            onChange={e => onRename(e.target.value)}
            disabled={locked}
          />
          <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: 3 }}>
            {row.file.name} · {formatBytes(row.file.size)}
          </div>
        </div>

        <div style={{ flex: 1.4, minWidth: 180 }}>
          <Select
            inputSize="sm"
            options={IMPORT_FORMATS}
            value={row.format}
            onChange={v => onFormat(v as MigrateFormat)}
            isDisabled={locked || busy}
          />
          {isDetected && (
            <div style={{ fontSize: fontSize.xs, color: colors.primary, marginTop: 3 }}>
              Detected{analysis?.hint ? ` · ${analysis.hint}` : ''}
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 120 }}>
          {analysis ? (
            <button
              type="button"
              onClick={onToggle}
              style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                color: colors.primary, fontSize: fontSize.sm,
              }}
            >
              {monikers.length} variable{monikers.length === 1 ? '' : 's'}
              {monikers.length > 0 && (row.expanded ? ' ▴' : ' ▾')}
            </button>
          ) : (
            <span style={{ fontSize: fontSize.sm, color: colors.textMuted }}>—</span>
          )}
          {formatted.length > 0 && (
            <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: 3 }}>
              {formatted.length} formatted
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <Button
            variant="secondary"
            size="sm"
            icon={<FontAwesomeIcon icon={faEye} />}
            onClick={onPreview}
            disabled={locked || busy || row.status === 'failed'}
          >
            Preview
          </Button>
          <button
            type="button"
            onClick={onRemove}
            disabled={locked}
            aria-label={`Remove ${row.file.name}`}
            style={{
              width: 28, height: 28, border: `1px solid ${colors.border}`, borderRadius: radius.sm,
              background: 'none', color: colors.textMuted, cursor: locked ? 'default' : 'pointer',
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      </div>

      {busy && (
        <ProgressBar
          size="sm"
          value={row.status === 'inspecting' ? undefined : row.progress}
          label={row.status === 'inspecting' ? 'Reading template…' : 'Uploading…'}
          style={{ marginTop: spacing.sm }}
        />
      )}

      {row.status === 'imported' && (
        <div style={{ fontSize: fontSize.sm, color: colors.success, marginTop: spacing.sm }}>
          <FontAwesomeIcon icon={faCheck} /> Imported
        </div>
      )}

      {row.error && (
        <div style={{ fontSize: fontSize.sm, color: colors.error, marginTop: spacing.sm }}>
          <FontAwesomeIcon icon={faCircleExclamation} /> {row.error}
        </div>
      )}

      {clashes.length > 0 && (
        <div style={{ fontSize: fontSize.sm, color: colors.error, marginTop: spacing.sm }}>
          <FontAwesomeIcon icon={faCircleExclamation} /> Cannot import — {clashes.length} moniker
          {clashes.length === 1 ? '' : 's'} clash after normalization:{' '}
          {clashes.map(c => c.conflictingSpellings.map(s => s.rawName).join(' vs ')).join('; ')}
        </div>
      )}

      {analysis && analysis.warnings.length > 0 && (
        <ul style={{
          margin: `${spacing.sm} 0 0`, paddingLeft: 18,
          fontSize: fontSize.xs, color: colors.warning,
        }}>
          {analysis.warnings.map((w, i) => (
            <li key={i}><FontAwesomeIcon icon={faTriangleExclamation} /> {w.replace(/^WARNING:\s*/, '')}</li>
          ))}
        </ul>
      )}

      {row.expanded && analysis && analysis.monikers.length > 0 && (
        <div style={{ marginTop: spacing.sm }}>
          {/* Formatted fields get their own rows: the converted chain plus what it renders, so a
              migrated formatting call can be checked without hunting for it in the preview. */}
          {formatted.length > 0 && (
            <div style={{ marginBottom: spacing.sm }}>
              <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginBottom: 4 }}>
                Formatted values
              </div>
              <table style={{ borderCollapse: 'collapse', fontSize: fontSize.xs, width: '100%' }}>
                <tbody>
                  {formatted.map(m => (
                    <tr key={m.normalizedKey}>
                      <td style={{ padding: '2px 8px 2px 0', whiteSpace: 'nowrap' }}>
                        <code style={{ fontFamily: fontFamily.mono, color: colors.primary }}>
                          {`{{${m.rawName} | ${m.formatters}}}`}
                        </code>
                      </td>
                      <td style={{ padding: '2px 6px', color: colors.textMuted }}>renders</td>
                      <td style={{ padding: '2px 0', width: '100%' }}>
                        <code style={{ fontFamily: fontFamily.mono, color: colors.text }}>{m.sample}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {plain.map(m => (
            <code
              key={m.normalizedKey}
              title={`${m.kind} · ${m.occurrenceCount} occurrence${m.occurrenceCount === 1 ? '' : 's'}`}
              style={{
                fontFamily: fontFamily.mono,
                fontSize: fontSize.xs,
                padding: '2px 6px',
                borderRadius: radius.sm,
                background: m.kind === 'Scalar' ? colors.background : colors.primaryLight,
                color: m.kind === 'Scalar' ? colors.textSecondary : colors.primary,
                border: `1px solid ${colors.border}`,
              }}
            >
              {`{{${m.rawName}}}`}
            </code>
            ))}
          </div>

          {mappings.length > 0 && (
            <details style={{ marginTop: spacing.sm }}>
              <summary style={{ fontSize: fontSize.xs, color: colors.primary, cursor: 'pointer' }}>
                Conversion log ({mappings.length} tag{mappings.length === 1 ? '' : 's'} rewritten)
              </summary>
              <ul style={{
                margin: `4px 0 0`, paddingLeft: 18,
                fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.textSecondary,
              }}>
                {mappings.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  )
}

function Banner({ tone, children }: { tone: 'error' | 'warning'; children: React.ReactNode }) {
  const palette = tone === 'error'
    ? { bg: colors.errorLight, fg: colors.error, border: colors.errorBorder }
    : { bg: colors.warningLight, fg: colors.warning, border: colors.warningBorder }
  return (
    <div style={{
      background: palette.bg, color: palette.fg, border: `1px solid ${palette.border}`,
      borderRadius: radius.sm, padding: `${spacing.sm} ${spacing.md}`,
      fontSize: fontSize.sm, marginBottom: spacing.md,
    }}>
      {children}
    </div>
  )
}
